import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, FileText, ClipboardList, Home, Loader2, X } from 'lucide-react';
import { searchApi } from '../api';

const sections = [
  { key: 'clients',   icon: User,          label: 'Clientes',  path: (item) => `/clientes/${item.id}`,   title: (i) => i.name,           sub: (i) => [i.document, i.phone, i.email].filter(Boolean).join(' • ') },
  { key: 'proposals', icon: FileText,      label: 'Propostas', path: () => '/orcamentos',                title: (i) => `${i.number || 'PROP'} — ${i.title || ''}`, sub: (i) => i.status || '' },
  { key: 'orders',    icon: ClipboardList, label: 'OS',        path: (item) => `/os/${item.id}`,         title: (i) => i.code || i.id,   sub: (i) => i.description || i.pest_type || '' },
  { key: 'units',     icon: Home,          label: 'Imóveis',   path: (item) => `/clientes/${item.client_id}`, title: (i) => i.name,       sub: (i) => [i.type, i.address].filter(Boolean).join(' • ') },
];

export default function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const nav = useNavigate();
  const rootRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Global shortcut: Ctrl/Cmd + K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => rootRef.current?.querySelector('input')?.focus(), 30);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try { setLoading(true); setResults(await searchApi.global(q.trim())); }
      catch { setResults({ clients: [], proposals: [], orders: [], units: [] }); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const goto = (path) => { setOpen(false); setQ(''); nav(path); };
  const totalHits = results ? Object.values(results).reduce((s, arr) => s + (arr?.length || 0), 0) : 0;

  return (
    <div ref={rootRef} className="relative w-full max-w-md" data-testid="global-search">
      <button
        onClick={() => { setOpen(true); setTimeout(() => rootRef.current?.querySelector('input')?.focus(), 30); }}
        className="w-full flex items-center gap-2 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-[12px] text-slate-500"
        data-testid="global-search-trigger"
      >
        <Search className="w-3.5 h-3.5" />
        <span className="flex-1 text-left truncate">Buscar clientes, propostas, OS, imóveis…</span>
        <kbd className="hidden lg:inline text-[10px] px-1.5 py-0.5 bg-white border border-slate-200 rounded text-slate-500">⌘K</kbd>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-40 w-[min(560px,90vw)]" data-testid="global-search-dropdown">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              value={q} onChange={e => setQ(e.target.value)}
              placeholder="Digite ao menos 2 caracteres…"
              className="flex-1 bg-transparent outline-none text-[13px] py-1"
              data-testid="global-search-input"
            />
            {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-200 rounded" data-testid="global-search-close">
              <X className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {q.trim().length < 2 ? (
              <div className="p-4 text-[12px] text-slate-500 text-center">
                Comece digitando. Suporta nome, CPF/CNPJ, telefone, e-mail, código de OS, título de proposta.
              </div>
            ) : results && totalHits === 0 ? (
              <div className="p-4 text-[12px] text-slate-500 text-center">Nenhum resultado para "{q}".</div>
            ) : results ? (
              sections.map(sec => {
                const items = results[sec.key] || [];
                if (items.length === 0) return null;
                const Icon = sec.icon;
                return (
                  <div key={sec.key} className="border-b border-slate-100 last:border-b-0">
                    <div className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/60">
                      {sec.label} ({items.length})
                    </div>
                    {items.map(it => (
                      <button
                        key={`${sec.key}-${it.id}`}
                        onClick={() => goto(sec.path(it))}
                        className="w-full flex items-start gap-2.5 px-3 py-2 hover:bg-emerald-50 text-left"
                        data-testid={`search-result-${sec.key}-${it.id}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0 mt-0.5">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-slate-900 truncate">{sec.title(it)}</div>
                          <div className="text-[11px] text-slate-500 truncate">{sec.sub(it)}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
